import React, { useState, useEffect, useMemo } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Link2, Check, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

const VARIABLE_TYPES = {
  'independent': { label: 'Independent Variable', shortLabel: 'Independent Variable', color: '#22c55e' },
  'dependent': { label: 'Dependent Variable', shortLabel: 'Dependent Variable', color: '#ef4444' },
  'mediator': { label: 'Mediator', shortLabel: 'Mediator', color: '#f59e0b' },
  'moderator': { label: 'Moderator', shortLabel: 'Moderator', color: '#8b5cf6' },
  'control': { label: 'Control Variable', shortLabel: 'Control Variable', color: '#6b7280' }
};

const C4_TheoryLinkage = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  
  const theories = dataC?.theoreticalFoundations?.filter(t => t.name.trim()) || [];
  const constructs = useMemo(() => dataC?.constructs || [], [dataC?.constructs]);
  const levels = useMemo(() => dataC?.levels || [], [dataC?.levels]);
  
  const [expandedTheory, setExpandedTheory] = useState(theories.length > 0 ? 0 : null);
  
  // Use context as single source of truth - local state mirrors it
  const theoryLinks = dataC?.theoryLinks || {};
  
  // Helper to apply updates to both local display and context storage
  const applyTheoryLinkUpdate = (newLinks) => {
    updateDataRaw({ 'wizardC.theoryLinks': newLinks });
  };

  // Clean up invalid construct/level references when they change
  useEffect(() => {
    // Get the latest theoryLinks from context to avoid stale closures
    const currentLinks = dataC?.theoryLinks || {};
    if (Object.keys(currentLinks).length === 0) return;
    
    // Don't run cleanup if constructs and levels are still loading (empty)
    // This prevents wiping out theoryLinks data on initial mount
    if (constructs.length === 0 && levels.length === 0) return;
    
    const constructIds = new Set(constructs.map(c => c.id));
    const levelIds = new Set(levels);
    let hasChanges = false;
    const cleanedLinks = {};
    
    Object.keys(currentLinks).forEach(theoryIndex => {
      const link = currentLinks[theoryIndex];
      if (!link) return;
      
      // Only filter constructs if we have constructs defined
      const validConstructs = constructs.length > 0 
        ? (link.linkedConstructs || []).filter(id => constructIds.has(id))
        : (link.linkedConstructs || []);
      
      // Only filter levels if we have levels defined
      const validLevels = levels.length > 0
        ? (link.linkedLevels || []).filter(id => levelIds.has(id))
        : (link.linkedLevels || []);
      
      if (validConstructs.length !== (link.linkedConstructs || []).length ||
          validLevels.length !== (link.linkedLevels || []).length) {
        hasChanges = true;
      }
      
      cleanedLinks[theoryIndex] = {
        ...link,
        linkedConstructs: validConstructs,
        linkedLevels: validLevels
      };
    });
    
    if (hasChanges) {
      applyTheoryLinkUpdate(cleanedLinks);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constructs, levels, dataC?.theoryLinks]);

  const getLevelLabel = (levelId) => {
    const levelMap = {
      'individual': 'Individual',
      'interpersonal': 'Interpersonal',
      'team': 'Team',
      'organizational': 'Organizational',
      'institutional': 'Institutional',
      'societal': 'Societal'
    };
    return levelMap[levelId] || levelId;
  };

  const getTheoryLink = (theoryIndex) => {
    return theoryLinks[theoryIndex] || {
      linkedConstructs: [],
      linkedLevels: []
    };
  };

  const updateTheoryLink = (theoryIndex, updates) => {
    const currentLink = getTheoryLink(theoryIndex);
    const newLinks = {
      ...theoryLinks,
      [theoryIndex]: { ...currentLink, ...updates }
    };
    applyTheoryLinkUpdate(newLinks);
  };

  const toggleConstructLink = (theoryIndex, constructId) => {
    const currentLink = getTheoryLink(theoryIndex);
    const linkedConstructs = currentLink.linkedConstructs || [];
    const isLinked = linkedConstructs.includes(constructId);
    
    const newLinkedConstructs = isLinked
      ? linkedConstructs.filter(id => id !== constructId)
      : [...linkedConstructs, constructId];
    
    updateTheoryLink(theoryIndex, {
      linkedConstructs: newLinkedConstructs
    });
  };

  const getConstructType = (construct) => {
    return VARIABLE_TYPES[construct?.type] || VARIABLE_TYPES['independent'];
  };

  const toggleLevelLink = (theoryIndex, levelId) => {
    const currentLink = getTheoryLink(theoryIndex);
    const linkedLevels = currentLink.linkedLevels || [];
    const isLinked = linkedLevels.includes(levelId);
    
    updateTheoryLink(theoryIndex, {
      linkedLevels: isLinked
        ? linkedLevels.filter(id => id !== levelId)
        : [...linkedLevels, levelId]
    });
  };

  const isConstructLinked = (theoryIndex, constructId) => {
    const link = getTheoryLink(theoryIndex);
    return (link.linkedConstructs || []).includes(constructId);
  };

  const isLevelLinked = (theoryIndex, levelId) => {
    const link = getTheoryLink(theoryIndex);
    return (link.linkedLevels || []).includes(levelId);
  };

  const getLinkedConstructsCount = (theoryIndex) => {
    const link = getTheoryLink(theoryIndex);
    return (link.linkedConstructs || []).length;
  };

  const getLinkedLevelsCount = (theoryIndex) => {
    const link = getTheoryLink(theoryIndex);
    return (link.linkedLevels || []).length;
  };

  const handleContinue = () => {
    // Data is already persisted in context on every change, just navigate
    nextStep();
  };

  if (theories.length === 0) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <div className="step-icon theory-linkage-icon">
            <Link2 size={32} />
          </div>
          <div>
            <h2>Step 4: Theory Linkage</h2>
            <p className="step-description">Link theories to constructs and levels of analysis</p>
          </div>
        </div>
        <div className="step-content">
          <div className="info-box warning">
            <p>No theories have been identified yet. Please go back to Step 3 to add your theoretical foundations.</p>
          </div>
        </div>
        <NavigationButtons
          onBack={previousStep}
          onContinue={handleContinue}
          canContinue={true}
          continueLabel="Skip to Relationship Mapping"
        />
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon theory-linkage-icon">
          <Link2 size={32} />
        </div>
        <div>
          <h2>Step 4: Theory Linkage</h2>
          <p className="step-description">Connect your theories to constructs and levels of analysis</p>
        </div>
      </div>

      <div className="step-content">
        <div className="concept-definition">
          <h3>Why Link Theories to Your Framework Elements?</h3>
          <p>
            Connecting theories to your constructs and levels of analysis creates a 
            <strong> theoretically grounded framework</strong>. This ensures every element in your 
            conceptual model has theoretical justification and helps identify theory-driven relationships.
          </p>
        </div>

        <div className="info-box notice">
          <AlertCircle size={18} />
          <p>
            <strong>Note:</strong> Variable types (Independent Variable, Dependent Variable, Mediator, etc.) are carried over from Step 1 to ensure consistency. 
            If you need to change a construct's variable type, please go back to <strong>Step 1: Extract Constructs</strong>.
          </p>
        </div>

        <div className="theory-linkage-section">
          <h4>Your Theories ({theories.length})</h4>
          <p className="section-instruction">
            For each theory, select which constructs it informs and link to applicable levels of analysis.
          </p>

          <div className="theory-linkage-list">
            {theories.map((theory, theoryIndex) => (
              <div key={theoryIndex} className="theory-linkage-card">
                <div 
                  className="theory-linkage-header"
                  onClick={() => setExpandedTheory(expandedTheory === theoryIndex ? null : theoryIndex)}
                >
                  <div className="theory-linkage-title">
                    <span className="theory-connection-badge">{theory.connection}</span>
                    <h5>{theory.name}</h5>
                    <span className="theory-author">{theory.author}</span>
                  </div>
                  <div className="theory-linkage-summary">
                    <span className="linkage-count">
                      {getLinkedConstructsCount(theoryIndex)} constructs, {getLinkedLevelsCount(theoryIndex)} levels
                    </span>
                    {expandedTheory === theoryIndex ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {expandedTheory === theoryIndex && (
                  <div className="theory-linkage-body">
                    <div className="linkage-section">
                      <h6>Link to Constructs</h6>
                      {constructs.length === 0 ? (
                        <p className="no-items-message">No constructs defined. Go back to Step 1 to add constructs.</p>
                      ) : (
                        <div className="construct-linkage-grid">
                          {constructs.map(construct => {
                            const typeInfo = getConstructType(construct);
                            return (
                              <div 
                                key={construct.id} 
                                className={`construct-link-item ${isConstructLinked(theoryIndex, construct.id) ? 'linked' : ''}`}
                                style={{ borderLeftColor: typeInfo.color }}
                              >
                                <div className="construct-link-header">
                                  <label className="construct-link-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={isConstructLinked(theoryIndex, construct.id)}
                                      onChange={() => toggleConstructLink(theoryIndex, construct.id)}
                                    />
                                    <span className="construct-name">{construct.name}</span>
                                  </label>
                                  {isConstructLinked(theoryIndex, construct.id) && (
                                    <Check size={16} className="linked-check" />
                                  )}
                                </div>
                                <div 
                                  className="construct-type-badge"
                                  style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color, borderColor: typeInfo.color }}
                                >
                                  {typeInfo.label}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="linkage-section">
                      <h6>Link to Levels of Analysis</h6>
                      {levels.length === 0 ? (
                        <p className="no-items-message">No levels selected. Go back to Step 2 to select levels.</p>
                      ) : (
                        <div className="level-linkage-grid">
                          {levels.map(levelId => (
                            <label 
                              key={levelId} 
                              className={`level-link-item ${isLevelLinked(theoryIndex, levelId) ? 'linked' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isLevelLinked(theoryIndex, levelId)}
                                onChange={() => toggleLevelLink(theoryIndex, levelId)}
                              />
                              <span>{getLevelLabel(levelId)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="linkage-preview">
                      <h6>Theory-Construct Linkage Preview</h6>
                      {getLinkedConstructsCount(theoryIndex) === 0 ? (
                        <p className="preview-empty">Link constructs above to see the preview</p>
                      ) : (
                        <div className="preview-content">
                          <div className="preview-theory-name">{theory.name}</div>
                          <div className="preview-arrows">
                            {getTheoryLink(theoryIndex).linkedConstructs.map(constructId => {
                              const construct = constructs.find(c => c.id === constructId);
                              const typeInfo = getConstructType(construct);
                              return (
                                <div key={constructId} className="preview-link">
                                  <span className="preview-arrow">→</span>
                                  <span 
                                    className="preview-construct"
                                    style={{ borderColor: typeInfo.color, backgroundColor: `${typeInfo.color}15` }}
                                  >
                                    {construct?.name || 'Unknown'}
                                    <span className="preview-role">({typeInfo.shortLabel})</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {getLinkedLevelsCount(theoryIndex) > 0 && (
                            <div className="preview-levels">
                              <span className="preview-levels-label">Applicable at:</span>
                              {getTheoryLink(theoryIndex).linkedLevels.map(levelId => (
                                <span key={levelId} className="preview-level-badge">{getLevelLabel(levelId)}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="variable-roles-legend">
          <h4>Variable Type Reference</h4>
          <p className="legend-note">These types were assigned in Step 1 and are shown here for reference.</p>
          <div className="roles-grid">
            {Object.entries(VARIABLE_TYPES).map(([id, typeInfo]) => (
              <div key={id} className="role-item">
                <span className="role-color" style={{ backgroundColor: typeInfo.color }}></span>
                <div className="role-info">
                  <span className="role-name">{typeInfo.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="tip-box">
          <strong>Tip:</strong> The theory-construct linkages you create here will be highlighted in the 
          Relationship Mapping step, helping you visualize which relationships are theoretically grounded.
          To change a construct's variable type, go back to Step 1.
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={true}
        continueLabel="Continue to Relationship Mapping"
      />
    </div>
  );
};

export default C4_TheoryLinkage;
